import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import { Avatar } from 'primereact/avatar';
import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';
import { useNavigate } from 'react-router-dom';
import TeacherBatchAssignmentModal from './parts/TeacherBatchAssignmentModal';
import { useOperatorProfileStore } from '../../stateManagers/stores/operatorProfileStore';

/* ─── Design Tokens ────────────────────────────────────────────────── */
const C = {
  navy: '#0e225d',
  navyMid: '#1a3370',
  navyHover: 'rgba(14,34,93,0.06)',
  gold: '#fbb21f',
  goldDim: 'rgba(251,178,31,0.14)',
  goldBorder: 'rgba(251,178,31,0.35)',
  white: '#ffffff',
  border: '#e5e7eb',
  muted: '#9ca3af',
  sub: '#6b7280',
  body: '#1f2937',
  green: '#16a34a',
  greenDim: 'rgba(22,163,74,0.12)',
  greenBorder: 'rgba(22,163,74,0.3)',
  red: '#dc2626',
  redDim: 'rgba(220,38,38,0.08)',
  redBorder: 'rgba(220,38,38,0.25)',
};

/* ─── Interfaces ───────────────────────────────────────────────────── */
interface ProfilePicture {
  file_name: string;
  key: string;
  cdn_url: string;
  dev_url: string;
  size: number;
  mime_type: string;
  etag: string;
  uploaded_at: { $date: { $numberLong: string } };
}

interface Teacher {
  teacher_id: string;
  name: string;
  dateOfBirth: string | null;
  gender: string | null;
  aadharNumber: string | null;
  category: string | null;
  mobileNumber: string | null;
  email: string;
  district: string | null;
  state: string | null;
  pinCode: string | null;
  maritalStatus: string | null;
  qualification: string | null;
  dateOfJoining: string | null;
  employeeId: string | null;
  experience: string | null;
  bloodGroup: string | null;
  whatsappNumber: string | null;
  status: string;
  created_at: string | null;
  profilePicture?: ProfilePicture | null;
  is_registered: boolean;
}

interface ApiResponse {
  success: boolean;
  data: Teacher[];
  pagination: { page: number; limit: number; total: number; pages: number };
}

/* ─── Main Component ────────────────────────────────────────────────── */
const Teachers: React.FC = () => {
  const navigate = useNavigate();
  const { profile } = useOperatorProfileStore();

  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [tempSearch, setTempSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 10, pages: 0 });
  const [selectedTeachers, setSelectedTeachers] = useState<Teacher[] | null>(null);
  const [showBatchAssignmentModal, setShowBatchAssignmentModal] = useState(false);
  const [selectedTeacherId, setSelectedTeacherId] = useState<string | null>(null);
  const [selectedTeacherName, setSelectedTeacherName] = useState<string | null>(null);

  useEffect(() => {
    fetchTeachers();
  }, [page, limit, search]);

  const fetchTeachers = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...(search && { search }),
      });

      const response = await apiClient.get<ApiResponse>(`/v1/teachers/get-teachers?${params}`);
      if (response.data.success) {
        setTeachers(response.data.data);
        setPagination(response.data.pagination);
      } else {
        setError('Failed to load teachers');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load teachers');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setSearch(tempSearch);
    setPage(1);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '—';
    try {
      return new Date(dateString).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
      return dateString;
    }
  };

  const handleAssignBatches = (teacherId: string, teacherName: string) => {
    setSelectedTeacherId(teacherId);
    setSelectedTeacherName(teacherName);
    setShowBatchAssignmentModal(true);
  };

  /* ── Column Templates ── */
  const profilePictureTemplate = (row: Teacher) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
      <div style={{ position: 'relative', flexShrink: 0 }}>
        <Avatar
          image={row.profilePicture?.cdn_url || undefined}
          icon={!row.profilePicture ? 'pi pi-user' : undefined}
          size="normal"
          shape="circle"
          style={{
            width: '42px',
            height: '42px',
            backgroundColor: row.profilePicture ? 'transparent' : '#e8eef7',
            color: C.navy,
            border: `2px solid ${C.border}`,
          }}
        />
        {row.is_registered && (
          <div
            style={{
              position: 'absolute',
              bottom: '-2px',
              right: '-2px',
              width: '14px',
              height: '14px',
              background: C.green,
              borderRadius: '50%',
              border: '2px solid white',
            }}
          />
        )}
      </div>
      <div>
        <div style={{ fontWeight: '600', color: C.navy, fontSize: '0.87rem' }}>{row.name}</div>
        {row.employeeId && (
          <div style={{ fontSize: '0.73rem', color: C.muted, fontFamily: 'monospace' }}>
            {row.employeeId}
          </div>
        )}
      </div>
    </div>
  );

  const statusTemplate = (row: Teacher) => (
    <span
      style={{
        padding: '4px 12px',
        borderRadius: '20px',
        fontSize: '0.73rem',
        fontWeight: '700',
        textTransform: 'capitalize',
        ...(row.status === 'active'
          ? { background: C.greenDim, color: C.green, border: `1px solid ${C.greenBorder}` }
          : { background: C.redDim, color: C.red, border: `1px solid ${C.redBorder}` }),
      }}
    >
      {row.status}
    </span>
  );

  const registrationTemplate = (row: Teacher) => (
    <span
      style={{
        padding: '4px 12px',
        borderRadius: '20px',
        fontSize: '0.73rem',
        fontWeight: '700',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '5px',
        ...(row.is_registered
          ? { background: 'rgba(14,34,93,0.08)', color: C.navy, border: '1px solid rgba(14,34,93,0.2)' }
          : { background: C.goldDim, color: '#b8860b', border: `1px solid ${C.goldBorder}` }),
      }}
    >
      <i className={row.is_registered ? 'pi pi-check-circle' : 'pi pi-exclamation-triangle'} style={{ fontSize: '0.68rem' }} />
      {row.is_registered ? 'Registered' : 'Not Registered'}
    </span>
  );

  const actionsTemplate = (row: Teacher) => (
    <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
      {[
        { icon: 'pi pi-eye', title: 'View', hover: C.navyHover, color: C.navy, action: () => navigate(`/teachers/${row.teacher_id}`) },
        { icon: 'pi pi-pencil', title: 'Edit', hover: C.goldDim, color: '#b8860b', action: () => navigate(`/teachers/edit/${row.teacher_id}`) },
        { icon: 'pi pi-users', title: 'Assign Batches', hover: C.greenDim, color: C.green, action: () => handleAssignBatches(row.teacher_id, row.name) },
      ].map(({ icon, title, hover, color, action }) => (
        <button
          key={icon}
          onClick={action}
          title={title}
          style={{
            width: '32px',
            height: '32px',
            background: 'transparent',
            border: `1px solid ${C.border}`,
            borderRadius: '8px',
            cursor: 'pointer',
            color: C.muted,
            transition: 'all 0.15s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = hover;
            e.currentTarget.style.color = color;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.color = C.muted;
          }}
        >
          <i className={icon} style={{ fontSize: '0.82rem' }} />
        </button>
      ))}
    </div>
  );

  const renderPagination = () => {
    const total = pagination.pages;
    const pages: (number | string)[] = [];

    if (total <= 5) {
      for (let i = 1; i <= total; i++) pages.push(i);
    } else if (page <= 3) {
      pages.push(1, 2, 3, '...', total);
    } else if (page >= total - 2) {
      pages.push(1, '...', total - 2, total - 1, total);
    } else {
      pages.push(1, '...', page, '...', total);
    }

    return pages.map((p, i) =>
      p === '...' ? (
        <span key={`e${i}`} style={{ padding: '0 4px', color: C.muted, fontSize: '0.82rem' }}>…</span>
      ) : (
        <button
          key={p}
          onClick={() => setPage(p as number)}
          style={{
            minWidth: '34px',
            height: '34px',
            padding: '0 8px',
            borderRadius: '8px',
            fontSize: '0.83rem',
            fontWeight: page === p ? '700' : '500',
            background: page === p ? C.navy : 'transparent',
            color: page === p ? C.gold : C.navy,
            border: page === p ? 'none' : `1px solid ${C.border}`,
            cursor: 'pointer',
            transition: 'all 0.15s ease',
          }}
          onMouseEnter={(e) => { if (page !== p) e.currentTarget.style.background = C.navyHover; }}
          onMouseLeave={(e) => { if (page !== p) e.currentTarget.style.background = 'transparent'; }}
        >
          {p}
        </button>
      )
    );
  };

  if (loading && teachers.length === 0) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px', background: C.white, borderRadius: '16px', border: `1px solid ${C.border}` }}>
        <div style={{ textAlign: 'center' }}>
          <ProgressSpinner style={{ width: '44px', height: '44px' }} strokeWidth="3" animationDuration=".8s" />
          <p style={{ marginTop: '14px', color: C.navy, fontSize: '0.9rem', fontWeight: '500' }}>Loading teachers…</p>
        </div>
      </div>
    );
  }

  const selected = selectedTeachers ?? [];

  return (
    <>
      <style>{`
        .teachers-dt .p-datatable-thead > tr > th {
          background: #fafafa !important; 
          color: ${C.muted} !important;
          font-size: 0.71rem !important; 
          font-weight: 700 !important;
          text-transform: uppercase !important; 
          letter-spacing: 0.6px !important;
          border-bottom: 2px solid ${C.border} !important; 
          padding: 14px 16px !important;
        }
        .teachers-dt .p-datatable-tbody > tr:hover > td { background: rgba(14,34,93,0.025) !important; }
        .teachers-dt .p-datatable-tbody > tr > td { 
          border-bottom: 1px solid #f3f4f6 !important; 
          padding: 10px 12px !important; 
        }
        .teachers-dt .p-checkbox .p-checkbox-box.p-highlight { background: ${C.navy} !important; border-color: ${C.navy} !important; }
      `}</style>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {/* Top Toolbar - Now matching Students page structure */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            {/* Table View Toggle */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '10px 14px',
              background: C.white,
              border: `1px solid ${C.border}`,
              borderRadius: '9px',
              fontSize: '0.82rem',
              fontWeight: '600',
              color: C.navy,
              cursor: 'pointer',
              boxShadow: '0 1px 3px rgba(14,34,93,0.06)'
            }}>
              <i className="pi pi-table" style={{ fontSize: '0.8rem' }} />
              Table View
              <i className="pi pi-chevron-down" style={{ fontSize: '0.65rem', marginLeft: '2px', color: C.muted }} />
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            {/* Search */}
            <div style={{ display: 'flex', alignItems: 'center', background: C.white, border: `1px solid ${C.border}`, borderRadius: '9px', overflow: 'hidden' }}>
              <input
                value={tempSearch}
                onChange={(e) => setTempSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Search teachers by name, employee ID..."
                style={{
                  padding: '10px 12px',
                  border: 'none',
                  outline: 'none',
                  fontSize: '0.82rem',
                  color: C.body,
                  minWidth: '260px',
                  background: 'transparent'
                }}
              />
              <button
                onClick={handleSearch}
                style={{ padding: '10px 14px', background: C.navy, border: 'none', color: C.gold, cursor: 'pointer', fontSize: '0.82rem', fontWeight: '700' }}
              >
                <i className="pi pi-search" style={{ fontSize: '0.78rem' }} />
              </button>
            </div>

            <button
              onClick={() => navigate('/teachers/add-teacher')}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '7px',
                padding: '10px 18px',
                background: C.navy,
                border: 'none',
                borderRadius: '9px',
                color: C.white,
                fontSize: '0.82rem',
                fontWeight: '700',
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(14,34,93,0.25)',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = C.navyMid; e.currentTarget.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = C.navy; e.currentTarget.style.transform = 'translateY(0)'; }}
            >
              <i className="pi pi-plus" style={{ fontSize: '0.82rem' }} />
              Add New Teacher
            </button>
          </div>
        </div>

        {error && <Message severity="error" text={error} style={{ width: '100%', fontSize: '0.88rem' }} />}

        {/* Main Table Card */}
        <div style={{ background: C.white, borderRadius: '14px', border: `1px solid ${C.border}`, boxShadow: '0 1px 4px rgba(14,34,93,0.06)', overflow: 'hidden' }}>
          {/* Table Header Row */}
          <div style={{ padding: '14px 18px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '0.8rem', color: C.sub }}>Showing per page</span>
              <select
                value={limit}
                onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
                style={{
                  padding: '4px 8px',
                  border: `1px solid ${C.border}`,
                  borderRadius: '7px',
                  fontSize: '0.8rem',
                  color: C.navy,
                  background: C.white,
                  fontWeight: '600',
                  cursor: 'pointer',
                  outline: 'none'
                }}
              >
                {[5, 10, 20, 50].map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>

            {/* Bulk Action (if needed) */}
            {selected.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 14px', background: '#f8f9ff', border: `1px solid ${C.border}`, borderRadius: '10px' }}>
                <span style={{ fontSize: '0.8rem', fontWeight: '700', color: C.navy }}>{selected.length} Selected</span>
                <button
                  onClick={() => {/* Add bulk action if required */}}
                  style={{ padding: '5px 12px', background: C.goldDim, border: `1px solid ${C.goldBorder}`, borderRadius: '7px', color: '#b8860b', fontSize: '0.78rem', fontWeight: '700', cursor: 'pointer' }}
                >
                  Bulk Action
                </button>
              </div>
            )}
          </div>

          {/* DataTable */}
          <DataTable
            value={teachers}
            selection={selectedTeachers}
            onSelectionChange={(e: any) => setSelectedTeachers(e.value)}
            dataKey="teacher_id"
            loading={loading}
            emptyMessage={
              <div style={{ textAlign: 'center', padding: '40px 20px', color: C.muted }}>
                <i className="pi pi-users" style={{ fontSize: '2rem', display: 'block', marginBottom: '10px', opacity: 0.4 }} />
                <span style={{ fontSize: '0.9rem' }}>No teachers found</span>
              </div>
            }
            className="teachers-dt"
            style={{ fontSize: '0.83rem' }}
          >
            <Column selectionMode="multiple" headerStyle={{ width: '3rem', padding: '12px 14px' }} bodyStyle={{ padding: '12px 14px' }} />
            <Column field="name" header="Teacher" body={profilePictureTemplate} headerStyle={{ minWidth: '280px' }} />
            <Column field="status" header="Status" body={statusTemplate} />
            <Column field="is_registered" header="Registration" body={registrationTemplate} headerStyle={{ minWidth: '150px' }} />
            <Column field="gender" header="Gender" body={(row) => <span style={{ color: C.sub }}>{row.gender ? row.gender.charAt(0).toUpperCase() + row.gender.slice(1) : '—'}</span>} />
            <Column field="dateOfJoining" header="Joining Date" body={(row) => <span style={{ color: C.sub }}>{formatDate(row.dateOfJoining)}</span>} />
            <Column header="Actions" body={actionsTemplate} headerStyle={{ width: '130px', textAlign: 'center' }} bodyStyle={{ textAlign: 'center' }} />
          </DataTable>

          {/* Pagination Footer */}
          {pagination.pages > 0 && (
            <div style={{ padding: '14px 18px', borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
              <span style={{ fontSize: '0.8rem', color: C.muted }}>
                Showing {((page - 1) * limit) + 1}–{Math.min(page * limit, pagination.total)} of {pagination.total} teachers
              </span>

              <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                <button onClick={() => setPage(1)} disabled={page === 1} style={{ width: '30px', height: '30px', background: 'transparent', border: `1px solid ${C.border}`, borderRadius: '7px', cursor: page === 1 ? 'not-allowed' : 'pointer', color: C.navy }}>
                  <i className="pi pi-angle-double-left" style={{ fontSize: '0.7rem' }} />
                </button>
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={{ width: '30px', height: '30px', background: 'transparent', border: `1px solid ${C.border}`, borderRadius: '7px', cursor: page === 1 ? 'not-allowed' : 'pointer', color: C.navy }}>
                  <i className="pi pi-angle-left" style={{ fontSize: '0.7rem' }} />
                </button>

                {renderPagination()}

                <button onClick={() => setPage(p => Math.min(pagination.pages, p + 1))} disabled={page === pagination.pages} style={{ width: '30px', height: '30px', background: 'transparent', border: `1px solid ${C.border}`, borderRadius: '7px', cursor: page === pagination.pages ? 'not-allowed' : 'pointer', color: C.navy }}>
                  <i className="pi pi-angle-right" style={{ fontSize: '0.7rem' }} />
                </button>
                <button onClick={() => setPage(pagination.pages)} disabled={page === pagination.pages} style={{ width: '30px', height: '30px', background: 'transparent', border: `1px solid ${C.border}`, borderRadius: '7px', cursor: page === pagination.pages ? 'not-allowed' : 'pointer', color: C.navy }}>
                  <i className="pi pi-angle-double-right" style={{ fontSize: '0.7rem' }} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      <TeacherBatchAssignmentModal
        visible={showBatchAssignmentModal}
        teacherId={selectedTeacherId || ''}
        teacherName={selectedTeacherName || ''}
        instituteId={profile?.institute_id}
        branchId={profile?.branch_id}
        onHide={() => setShowBatchAssignmentModal(false)}
        onSuccess={() => { setShowBatchAssignmentModal(false); fetchTeachers(); }}
      />
    </>
  );
};

export default Teachers;